import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Activity } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import { Button } from "../../../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { useSessionRunsContext } from "../../../../contexts/sessionRunsContext";
import {
  SessionRun,
  SessionRunState,
} from "../../../../models/audit/sessionRun";
import {
  EventRun,
  stopRequiresConfirmation,
} from "../../../../models/audit/eventRun";
import { Badge } from "../../../../components/ui/badge";
import { useHookCache } from "../../../../lib/hookCache";
import { AuditSession } from "../../../../models/audit/auditSession";
import { ProcedureSessionTableShell } from "../../../../models/audit/procedureSessionTableShell";
import {
  STOP_RESPONSE_UNCONFIRMED,
  STOP_RESPONSE_UNPARSED,
  useStopEventRun,
} from "../../../../hooks/useStopEventRun";
import { StopEventRunButton } from "../../../../components/audit/StopEventRunButton";
import { StopEventRunConfirmDialog } from "../../../../components/audit/StopEventRunConfirmDialog";
import {
  RunLogEntry,
  runLogEntries,
  runLogEntryColumnIds,
} from "../../../../lib/sessionRunUtils";

interface EventRunsPopoverProps {
  /** Owning control session — needed to refetch session runs after a stop.
   * Threaded as a prop from `AuditWorkpaperLeftPanel`, which already holds
   * `controlSession.id`. */
  controlSessionId: string;
  /** Reports the popover's *effective* open state: Radix open/close, plus a
   * synthesized `false` when the popover leaves the screen without Radix
   * firing one (unmount while open, or the empty-runs early return). */
  onOpenChange?: (open: boolean) => void;
}

// SessionRun states that represent work still in flight. Stop-button
// eligibility is computed directly off the row's `sessionRuns` against this
// list — NOT off `deriveEventRunState`'s display label, which checks
// failed/timed_out first. A partially-failed fan-out (one sample failed,
// others still leased/queued/dispatched/retry/local_run) would otherwise
// show "Failed" and hide the only stop control while the remaining samples
// keep running. The derived label stays purely a display concern.
const ACTIVE_SESSION_RUN_STATES: SessionRunState[] = [
  "queued",
  "dispatched",
  "leased",
  "retry",
  "local_run",
];

const hasActiveSessionRun = (sessionRuns: SessionRun[]): boolean =>
  sessionRuns.some((sr) => ACTIVE_SESSION_RUN_STATES.includes(sr.state));

// Dispatch-window eligibility: an event run whose fan-out hasn't written any
// session_run rows yet has nothing for `hasActiveSessionRun` to see, so fall
// back to the run's own top-level state — the same state-only rule
// BackgroundTasksPopover uses on its session-run-less surface.
const isActiveEventRunState = (state: EventRun["state"]): boolean =>
  state === "pending" || state === "processing";

const getStateColor = (state: SessionRunState): string => {
  switch (state) {
    case "succeeded":
      return "bg-green-100 text-green-800 border-green-300";
    case "failed":
    case "timed_out":
      return "bg-red-100 text-red-800 border-red-300";
    case "cancelled":
    case "superseded":
    case "unknown":
      return "bg-gray-100 text-gray-800 border-gray-300";
    case "queued":
    case "ready":
    case "dispatched":
      return "bg-blue-100 text-blue-800 border-blue-300";
    case "leased":
    case "retry":
    case "local_run":
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    default:
      return "bg-gray-100 text-gray-800 border-gray-300";
  }
};

const getStateLabel = (state: SessionRunState): string => {
  switch (state) {
    case "queued":
      return "Queued";
    case "dispatched":
      return "Dispatched";
    case "leased":
      return "Running";
    case "retry":
      return "Retrying";
    case "local_run":
      return "Local Run";
    case "ready":
      return "Ready";
    case "failed":
      return "Failed";
    case "timed_out":
      return "Timed Out";
    case "cancelled":
      return "Cancelled";
    case "superseded":
      return "Superseded";
    case "succeeded":
      return "Succeeded";
    case "unknown":
      return "Unknown";
    default:
      return state;
  }
};

const aggregateSessionRunState = (
  sessionRuns: SessionRun[],
): SessionRunState => {
  // Failed outranks timed out: a failed row ran and errored with a reason the
  // auditor can act on, while a timed-out row says only that its lease lapsed.
  if (sessionRuns.some((r) => r.state === "failed")) {
    return "failed";
  }
  if (sessionRuns.some((r) => r.state === "timed_out")) {
    return "timed_out";
  }

  // If any are running, event run is running
  if (
    sessionRuns.some((r) => ["leased", "retry", "local_run"].includes(r.state))
  ) {
    return "leased";
  }

  // If any are queued or dispatched (awaiting or mid dispatch-window
  // fan-out), event run is still active.
  if (
    sessionRuns.some((r) => r.state === "queued" || r.state === "dispatched")
  ) {
    return "queued";
  }

  // If any are cancelled and none remain active, event run is cancelled.
  if (sessionRuns.some((r) => r.state === "cancelled")) {
    return "cancelled";
  }

  // Any child in a state this build doesn't recognize (schema-coerced to
  // "unknown") makes the aggregate Unknown rather than a false Ready.
  if (sessionRuns.some((r) => r.state === "unknown")) {
    return "unknown";
  }

  // If all succeeded, event run succeeded
  if (sessionRuns.every((r) => r.state === "succeeded")) {
    return "succeeded";
  }

  return "ready";
};

const deriveEventRunState = (
  eventRun: EventRun,
  sessionRuns: SessionRun[],
): SessionRunState => {
  // Dispatch window: no session_run rows yet. Present the run as active off
  // its own state instead of "ready" — pending maps to the queued styling,
  // processing to the running styling.
  if (sessionRuns.length === 0) {
    switch (eventRun.state) {
      case "pending":
        return "queued";
      case "processing":
        return "leased";
      // An event-run state this build doesn't recognize (coerced by the
      // schema's `.catch`) presents honestly as Unknown rather than Ready.
      case "unknown":
        return "unknown";
      default:
        return "ready";
    }
  }
  const state = aggregateSessionRunState(sessionRuns);
  // The event run's badge summarises every sample, and any sample that did not
  // finish, timed out or not, marks the whole run Failed.
  return state === "timed_out" ? "failed" : state;
};

// One sample's entry shows its row's own state. An entry with the analytics
// row folded in shows the first of these that applies: Failed if either row
// failed, then Timed Out if either timed out, even while the other is still
// active; Running while either runs; Queued while either waits to start;
// Cancelled if either was cancelled; Unknown if either is in a state this
// build does not know; Superseded when either was superseded and the other
// succeeded or was superseded too; Succeeded once both have; Ready otherwise.
const deriveEntryState = (entry: RunLogEntry): SessionRunState => {
  if (entry.runs.length === 1) {
    return entry.run.state;
  }
  const state = aggregateSessionRunState(entry.runs);
  // Kept out of the shared aggregate: the event run's badge reads Ready for a
  // superseded sample, and only an entry names the attempt that was replaced.
  if (
    state === "ready" &&
    entry.runs.some((r) => r.state === "superseded") &&
    entry.runs.every((r) => r.state === "superseded" || r.state === "succeeded")
  ) {
    return "superseded";
  }
  return state;
};

export const EventRunsPopover = ({
  controlSessionId,
  onOpenChange,
}: EventRunsPopoverProps) => {
  const {
    eventRuns,
    getSessionRunsForEventRun,
    highlightCellsForEventRun,
    highlightCellsForSessionRun,
    highlightCells,
    fetchSessionRuns,
    cancelSessionRunsBurst,
  } = useSessionRunsContext();
  const { getEntry } = useHookCache();
  const { stopEventRun, isStopping } = useStopEventRun();
  const [expandedEventRuns, setExpandedEventRuns] = useState<Set<string>>(
    new Set(),
  );
  // The event run awaiting workpaper-scope confirmation, if any.
  const [confirmDialogEventRun, setConfirmDialogEventRun] =
    useState<EventRun | null>(null);
  // Scopes `loading` to the buttons whose stops are in flight — a Set, so a
  // second concurrent stop doesn't clobber the first row's spinner.
  const [stoppingEventRunIds, setStoppingEventRunIds] = useState<Set<string>>(
    new Set(),
  );

  // Ref'd so the close-synthesizing effects below don't re-run when the
  // caller passes a fresh callback.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  // Last state we reported — dedupes the synthesized closes so the caller
  // only ever sees real transitions.
  const reportedOpenRef = useRef(false);
  const reportOpenChange = useCallback(
    (open: boolean) => {
      if (reportedOpenRef.current === open) return;
      // Only a closed→open transition refetches — `reportedOpenRef` already
      // dedupes no-op calls above, and the two callers of a `false` value
      // (Radix's real close, plus the synthesized-close effects below) never
      // reach here with `open: true`, so this can only fire once per open.
      const isOpeningTransition = open && !reportedOpenRef.current;
      reportedOpenRef.current = open;
      onOpenChangeRef.current?.(open);
      // Refresh on open rather than trusting whatever the shared context
      // last fetched — the popover can be reopened well after its last
      // open, and a Generate Workpaper click elsewhere on the page relies
      // on this same context but has no way to know this popover is (or
      // isn't) mounted.
      if (isOpeningTransition) {
        fetchSessionRuns(controlSessionId);
      }
    },
    [controlSessionId, fetchSessionRuns],
  );
  const hasEventRuns = eventRuns.length > 0;
  // Radix fires no onOpenChange when the open popover simply stops rendering:
  // unmount (e.g. the focus-mode branch flip re-parents the header) or the
  // empty-runs early return below. Synthesize the close in both cases.
  useEffect(() => () => reportOpenChange(false), [reportOpenChange]);
  useEffect(() => {
    if (!hasEventRuns) reportOpenChange(false);
  }, [hasEventRuns, reportOpenChange]);

  const sortedEventRuns = useMemo(() => {
    return [...eventRuns].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [eventRuns]);

  const toggleExpand = (eventRunId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedEventRuns((prev) => {
      const next = new Set(prev);
      if (next.has(eventRunId)) {
        next.delete(eventRunId);
      } else {
        next.add(eventRunId);
      }
      return next;
    });
  };

  const handleEventRunClick = (eventRunId: string) => {
    highlightCellsForEventRun(eventRunId);
  };

  const handleSessionRunClick = (e: React.MouseEvent, sessionRunId: string) => {
    e.stopPropagation();
    highlightCellsForSessionRun(sessionRunId);
  };

  // First uuid block — short enough for the popover row, unique enough for
  // support to grep prod logs with; the full id is what the click copies.
  const shortRunId = (runId: string): string => runId.slice(0, 8);

  // Only toast success once the clipboard write actually resolved —
  // clipboard access can be denied or unavailable (insecure context), and a
  // false "copied" defeats the whole point of a support affordance.
  const handleCopyRunId = (e: React.MouseEvent, runId: string) => {
    e.stopPropagation();
    if (!navigator.clipboard) {
      toast.error("Clipboard unavailable in this browser", {
        id: "run-id-copied",
      });
      return;
    }
    navigator.clipboard
      .writeText(runId)
      .then(() => toast.success("Run id copied", { id: "run-id-copied" }))
      .catch(() =>
        toast.error("Couldn't copy run id", { id: "run-id-copied" }),
      );
  };

  // Calls the stop endpoint and applies the shared success UX: toast, skip
  // warning, refetch the session's runs (so the row flips to Cancelled), and
  // seed the highlight set with the reverted cells so they flash.
  const performStop = useCallback(
    async (eventRunId: string) => {
      setStoppingEventRunIds((prev) => new Set(prev).add(eventRunId));
      const response = await stopEventRun(eventRunId);
      setStoppingEventRunIds((prev) => {
        const next = new Set(prev);
        next.delete(eventRunId);
        return next;
      });
      setConfirmDialogEventRun(null);

      // Kill any pending burst timers and supersede any in-flight fetch
      // BEFORE the post-stop refetch, so a request issued pre-stop can't
      // land afterwards and repaint the rows as still running.
      cancelSessionRunsBurst();

      if (!response) {
        // Hard failure (hook already toasted it). The backend's stop is
        // several separate commits, so a throw partway can leave the run
        // fenced to cancelled with the rows still showing Running — refetch
        // so the UI reflects whatever actually landed.
        await fetchSessionRuns(controlSessionId);
        return;
      }

      if (
        response === STOP_RESPONSE_UNPARSED ||
        response === STOP_RESPONSE_UNCONFIRMED
      ) {
        // Stop landed but the response was unusable, or the transport failed
        // in a way that leaves the outcome unknown (hook already toasted
        // either way) — still refetch so a landed stop flips the rows to
        // Cancelled; there's no stopped scope to seed the highlight with.
        await fetchSessionRuns(controlSessionId);
        return;
      }

      if (response.alreadyFinished) {
        toast.info("Run had already finished — swept any stragglers");
      } else {
        toast.success("Run stopped");
      }
      if (response.skipped.length > 0) {
        toast.warning(
          `${response.skipped.length} cell(s) kept — edited or locked by a user`,
        );
      }

      await fetchSessionRuns(controlSessionId);
      highlightCells(response.stoppedScope);
    },
    [
      stopEventRun,
      cancelSessionRunsBurst,
      fetchSessionRuns,
      controlSessionId,
      highlightCells,
    ],
  );

  const handleStopClick = (eventRun: EventRun) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (stopRequiresConfirmation(eventRun.runMetadata)) {
      setConfirmDialogEventRun(eventRun);
      return;
    }
    void performStop(eventRun.id);
  };

  const getSampleNumber = (sessionRun: SessionRun): number | null => {
    // Phase 3: prefer the table-shell entry (light) for sample-number lookup
    // and fall back to the heavy hydrated entry if only it is populated.
    // Both keys share the `['procedure-session', psId]` prefix used by the
    // status hook's invalidations, so they stay consistent across
    // workPaperState transitions.
    const tableShellEntry = getEntry<ProcedureSessionTableShell>([
      "procedure-session",
      sessionRun.sessionId,
      "table-shell",
    ]);
    const tableShell = tableShellEntry?.data;
    if (tableShell) {
      const dryRow = tableShell.sourceRows.find(
        (sr) => sr.id === sessionRun.sourceRowId,
      );
      if (dryRow) return dryRow.sampleNumber ?? null;
    }

    const entry = getEntry<AuditSession>([
      "procedure-session",
      sessionRun.sessionId,
    ]);
    const procedureSession = entry?.data;
    if (!procedureSession) return null;
    const sourceRow = procedureSession.sourceRows.find(
      (sr) => sr.id === sessionRun.sourceRowId,
    );
    return sourceRow?.sampleNumber ?? null;
  };

  if (eventRuns.length === 0) {
    return null;
  }

  return (
    <>
      <Popover onOpenChange={reportOpenChange}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className="flex items-center gap-2"
              >
                <Activity className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>Run Log</TooltipContent>
        </Tooltip>
        <PopoverContent
          className="w-[500px] max-h-[600px] overflow-y-auto"
          align="start"
        >
          <div className="space-y-1">
            <h4 className="font-semibold text-sm mb-3">Run Logs</h4>
            {sortedEventRuns.length === 0 ? (
              <div className="text-sm text-gray-500 py-2">No runs found</div>
            ) : (
              sortedEventRuns.map((eventRun) => {
                const sessionRuns = getSessionRunsForEventRun(eventRun.id);
                const entries = runLogEntries(sessionRuns);
                const eventRunState = deriveEventRunState(
                  eventRun,
                  sessionRuns,
                );
                // Stop eligibility: active session runs (once fan-out has
                // written them) OR an active top-level state. An OR, not a
                // ternary — a run wedged on "processing" with all-terminal
                // children (the zombie case the backend documents Stop as
                // the recovery for) must still show the button, and the
                // dispatch window (no session runs yet) needs the state
                // fallback.
                const canStop =
                  hasActiveSessionRun(sessionRuns) ||
                  isActiveEventRunState(eventRun.state);
                const isExpanded = expandedEventRuns.has(eventRun.id);

                return (
                  <div
                    key={eventRun.id}
                    className="border rounded-md p-2 space-y-1"
                  >
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 p-0"
                        onClick={(e) => toggleExpand(eventRun.id, e)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                      <div
                        className="flex-1 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2"
                        onClick={() => handleEventRunClick(eventRun.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Run</span>
                            <Badge
                              variant="outline"
                              className={`text-xs ${getStateColor(eventRunState)}`}
                            >
                              {getStateLabel(eventRunState)}
                            </Badge>
                          </div>
                          <span className="text-xs text-gray-500">
                            {entries.length} sample
                            {entries.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(eventRun.createdAt).toLocaleString()}
                        </div>
                      </div>
                      {canStop && (
                        <StopEventRunButton
                          onClick={handleStopClick(eventRun)}
                          isStopping={stoppingEventRunIds.has(eventRun.id)}
                        />
                      )}
                    </div>

                    {isExpanded && entries.length > 0 && (
                      <div className="ml-7 space-y-1 mt-2">
                        {[...entries]
                          .sort((a, b) => {
                            const sampleA = getSampleNumber(a.run);
                            const sampleB = getSampleNumber(b.run);
                            // Sort by sample number (nulls at the end)
                            if (sampleA === null && sampleB === null) return 0;
                            if (sampleA === null) return 1;
                            if (sampleB === null) return -1;
                            return sampleA - sampleB;
                          })
                          .map((entry) => {
                            const sessionRun = entry.run;
                            const sampleNumber = getSampleNumber(sessionRun);
                            const entryState = deriveEntryState(entry);
                            const columnIds = runLogEntryColumnIds(entry);
                            return (
                              <div
                                key={sessionRun.id}
                                className="border-l-2 border-gray-200 pl-3 py-1 cursor-pointer hover:bg-gray-50 rounded"
                                onClick={(e) =>
                                  handleSessionRunClick(e, sessionRun.id)
                                }
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium">
                                    {sampleNumber !== null
                                      ? `Sample ${sampleNumber}`
                                      : "Session Run"}
                                  </span>
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${getStateColor(entryState)}`}
                                  >
                                    {getStateLabel(entryState)}
                                  </Badge>
                                </div>
                                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                                  {/* Short run id, click-to-copy: an internal
                                      support affordance in the Run Log popover
                                      (deliberately not in the cell tooltip) so
                                      support can grep prod logs (MS-3366). */}
                                  <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="h-auto cursor-copy p-0 font-mono text-xs font-normal text-gray-500 hover:text-gray-800"
                                    title={`Copy run id ${sessionRun.id}`}
                                    onClick={(e) =>
                                      handleCopyRunId(e, sessionRun.id)
                                    }
                                  >
                                    Run {shortRunId(sessionRun.id)}
                                  </Button>
                                  {columnIds && (
                                    <span>Columns: {columnIds.length}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
      {confirmDialogEventRun && (
        <StopEventRunConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirmDialogEventRun(null);
          }}
          isStopping={isStopping}
          // Count EVERY sample under the run, not just the active subset: the
          // backend retro-cancels and reverts finished siblings too, so the
          // full set is what the stop touches. Counted by Run Log entry, so a
          // sample's analytics row is not a second sample. During the dispatch
          // window the list is still empty even though the stop does real
          // work, so omit the count rather than announce a no-op.
          sampleCount={
            runLogEntries(getSessionRunsForEventRun(confirmDialogEventRun.id))
              .length || undefined
          }
          onConfirm={() => void performStop(confirmDialogEventRun.id)}
        />
      )}
    </>
  );
};
